import { useEffect, useRef } from "react";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { isEqual, round, startCase, toLower, upperFirst } from "lodash";

import {
  AcquiredDateChoices,
  CreditNoteReasons,
  FilterBy,
  PaginatedOutput,
  ParticipantTypes,
  PaymentStatuses,
  ResaleStatuses,
  SortBy,
  SortDirection,
} from "@generated/graphql";

import { ParticipantWithSinglePseudonym } from "@components/account_profile/ParticipantDropdown";
import { groupedResalesSourcePageType } from "@components/grouped_resales/GroupedResales";
import { RoyaltiesSourcePageType } from "@components/royalties/Royalties";
import { mapStatusToDisplayFriendlyText } from "@util/resale";

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export type WatchCallback<T> = (current: T | undefined, previous: T | undefined) => void;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useWatch = <T extends unknown>(
  value: T,
  callback: WatchCallback<T>,
  updateDependencies: unknown[] = [],
): void => {
  const previous = usePrevious(value);

  useEffect(() => {
    if (previous !== value && !isEqual(previous, value)) {
      callback(value, previous);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, callback, previous, ...updateDependencies]);
};

export const isNullOrUndefined = (object: unknown) => object === null || object === undefined;
export const isTruthy = (object: unknown) => !!object;
export const isFalsy = (object: unknown) => !object;

// TODO: use something like this instead => https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
export const prefix0ToSingleDigitNumber = (number: number) => {
  return number.toString().padStart(2, "0");
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const filterDropdownTitle = (
  defaultValue: string,
  input: (ResaleStatuses | PaymentStatuses)[],
  isExternalUser: boolean,
) => {
  if (input === undefined || input.length === 0) {
    return defaultValue;
  } else {
    return getDisplayValueFromDBValue(input[0], isExternalUser).concat(
      input.length === 1 ? "" : `, + ${input.length - 1}`,
    );
  }
};

export const getDisplayValueFromDBValue = (
  input: ResaleStatuses | PaymentStatuses | CreditNoteReasons,
  isExternalUser?: boolean,
  showInvoiceRequestSent = false,
): string => {
  const mappedValue = mapStatusToDisplayFriendlyText({
    status: input,
    isExternalUser,
    showInvoiceRequestSent,
  });

  if (mappedValue?.label) {
    return mappedValue.label;
  }

  return upperFirst(input.toLowerCase().split("_").join(" "));
};

export const getParticipantTypesForDisplay = (participant_types?: ParticipantTypes[]) =>
  participant_types?.includes(ParticipantTypes.ART_CENTRE) ||
  participant_types?.includes(ParticipantTypes.CMO) ||
  participant_types?.includes(ParticipantTypes.AGENT)
    ? participant_types
        ?.filter((type) => type !== ParticipantTypes.BENEFICIARY)
        .map((type: string) => startCase(toLower(type)))
        .join(", ")
    : participant_types?.map((type: string) => startCase(toLower(type))).join(", ");

export enum DateFormat {
  DD_MM_YYYY_SLASH = "DD/MM/YYYY",
  YYYY_MM_DD_DASH = "YYYY-MM-DD",
  YYYY_MM_DD_DASH_WITH_TIME = "YYYY-MM-DD HH:mm:ss",
  DD_MM_YY_SLASH = "DD/MM/YY",
}

export const formatStringToDateFormat = (
  stringValue: string,
  dateformatEnum: DateFormat = DateFormat.DD_MM_YYYY_SLASH,
): string => {
  return dayjs(stringValue).format(dateformatEnum);
};

export const formatDateStringLong = (stringValue: string) =>
  formatStringToDateFormat(stringValue, DateFormat.DD_MM_YYYY_SLASH);

export type PaginatedOutputGeneric<TPagable> = Omit<PaginatedOutput, "results"> & {
  results: Array<TPagable>;
};

export type SortByTyped<T> = Omit<SortBy, "field"> & {
  field: keyof T;
};

export type FilterByTyped<T> = Omit<FilterBy, "field"> & {
  field: keyof T;
};

export enum ArtistAliveDisplay {
  YES = "Yes",
  NO = "No",
  UNKNOWN = "Unknown",
}

export const getDisplayValueForAcquiredDateChoices = (acquired_date_choice: AcquiredDateChoices) => {
  switch (acquired_date_choice) {
    case AcquiredDateChoices.NO:
      return "No";
    case AcquiredDateChoices.PRIMARY_SALE:
      return "Primary sale";
    case AcquiredDateChoices.YES:
      return "Yes";
    case AcquiredDateChoices.VOLUNTARY:
      return "Voluntary";
  }
  return "";
};

export const CURRENT_YEAR = dayjs().year();

export const formatISODateToFormat = ({ isoDate, targetDateFormat }: { isoDate: string; targetDateFormat: string }) => {
  return dayjs(isoDate).format(targetDateFormat);
};

export const formatPercentage = (value: number): string => {
  return round(value, 2).toFixed(2);
};

export const isArtist = (participantTypes: (ParticipantTypes | undefined)[]): boolean => {
  return participantTypes && participantTypes.includes(ParticipantTypes.ARTIST);
};

export const isBeneficiary = (participantTypes: (ParticipantTypes | undefined)[]): boolean => {
  return participantTypes && participantTypes.includes(ParticipantTypes.BENEFICIARY);
};

export const isArtCentre = (participantTypes: (ParticipantTypes | undefined)[]): boolean => {
  return participantTypes && participantTypes.includes(ParticipantTypes.ART_CENTRE);
};

export const isCMO = (participantTypes: (ParticipantTypes | undefined)[] | undefined): boolean => {
  return (participantTypes && participantTypes.includes(ParticipantTypes.CMO)) || false;
};

export const isAgent = (participantTypes: (ParticipantTypes | undefined)[] | undefined): boolean => {
  return (participantTypes && participantTypes.includes(ParticipantTypes.AGENT)) || false;
};

export const isAMP = (participantTypes: (ParticipantTypes | undefined)[] | undefined): boolean => {
  return (participantTypes && participantTypes.includes(ParticipantTypes.AMP)) || false;
};

export const documentKeyFromS3Location = (s3_location: string) => {
  const expression = new RegExp("[s3]+://([A-Za-z0-9]+(-[A-Za-z0-9]+)+)/");
  return s3_location.replace(expression, "");
};

export const documentInfoFromS3Location = (s3_location: string) => {
  const sections = documentKeyFromS3Location(s3_location).split("/");
  return {
    filename: sections[sections.length - 1],
    date: sections[sections.length - 1].substring(0, 8),
  };
};

export const formatValueToDisplayFriendly = (status: string | undefined) => {
  return startCase(status?.replace(/ /g, "_").toLowerCase());
};

export const getPageHeading = (
  sourcePage: RoyaltiesSourcePageType | groupedResalesSourcePageType | undefined,
  sourceFeature: "Royalties" | "GroupedResales",
): { heading: string; subheading: string } => {
  let defaultReturn = { heading: "", subheading: "" };
  if (sourceFeature === "GroupedResales") {
    defaultReturn = { heading: "Currently under review", subheading: "by the Resale Royalty team" };
  }
  switch (sourcePage) {
    case "UpcomingRoyalties":
      return { heading: "Future royalties", subheading: "" };
    case "PastPayments":
      return { heading: "Past payments", subheading: "" };
    case "PastReports":
      return { heading: "Past reports", subheading: "" };
    case "PastResaleReports":
      return { heading: "Past resale reports", subheading: "" };
    case "HoldPeriod":
      return { heading: "On hold", subheading: "" };
    case "PendingPayments":
      return { heading: "Pending payments", subheading: "" };
    case "ReportsToReview":
      return { heading: "Reports to review", subheading: "" };
    default:
      return defaultReturn;
  }
};

export const sortData = <T extends Record<string | number, unknown>>(
  data: T[],
  sortDirection: string,
  sortKey: keyof T,
): T[] => {
  return [...data].sort((a, b) => {
    const valA = a[sortKey];
    const valB = b[sortKey];

    // Handle null or undefined values by pushing them to the bottom
    if (valA == null) {
      return 1;
    }
    if (valB == null) {
      return -1;
    }

    if (typeof valA === "number" && typeof valB === "number") {
      return sortDirection === SortDirection.ASCENDING ? valA - valB : valB - valA;
    }

    if (typeof valA === "string" && typeof valB === "string") {
      return sortDirection === SortDirection.ASCENDING
        ? valA.localeCompare(valB, undefined, { sensitivity: "base" })
        : valB.localeCompare(valA, undefined, { sensitivity: "base" });
    }

    return 0; // If values are of mixed types, don't change order
  });
};

export const years: number[] = Array.from(new Array(CURRENT_YEAR - (1850 - 1)), (_val, index: number) => 1850 + index);

export const formatEnumLabel = (value: string): string => {
  const formattedValue = value.toLowerCase().replace(/_/g, " ");
  return formattedValue.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Converts a string with underscores to a friendly text format.
 * - Removes underscores
 * - Converts to title case (first letter of each word capitalized)
 *
 * @param {string} value - The input string to convert.
 * @returns {string} - The formatted friendly text.
 */
export const toFriendlyText = (value: string) => {
  return value
    .replace(/_/g, " ") // Replace underscores with spaces
    .split(" ") // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" "); // Join words back into a string
};

export const isArtistOrBeneficiary = (participantTypes: (ParticipantTypes | undefined)[]) => {
  return participantTypes && (isArtist(participantTypes) || isBeneficiary(participantTypes));
};

export const getDownloadFiles = (
  isExternalUser: boolean,
  curatedDocS3Location?: string,
  invoiceS3Location?: string,
) => {
  const files = [
    {
      id: "curated",
      name: curatedDocS3Location
        ? documentInfoFromS3Location(curatedDocS3Location).filename || "Curated stylesheet"
        : "No Curated Stylesheet Available",
      s3Location: curatedDocS3Location || "",
    },
    {
      id: "invoice",
      name: invoiceS3Location
        ? documentInfoFromS3Location(invoiceS3Location).filename || "Invoice Document"
        : "No Invoice Document Available",
      s3Location: invoiceS3Location || "",
    },
  ];

  return files.filter((file) => file.s3Location && (file.id !== "curated" || !isExternalUser));
};

export const isParticipantArtistOrBeneficiary = (participant: ParticipantWithSinglePseudonym): boolean => {
  const validTypes = [ParticipantTypes.ARTIST, ParticipantTypes.BENEFICIARY];
  return participant.participant_types.some((type) => validTypes.includes(type));
};

export const isParticipantAmpOrArtCentreOrCMO = (participant: ParticipantWithSinglePseudonym): boolean => {
  const validTypes = [ParticipantTypes.AMP, ParticipantTypes.ART_CENTRE, ParticipantTypes.CMO, ParticipantTypes.AGENT];
  return participant.participant_types.some((type) => validTypes.includes(type));
};

export const getDisplayNameForParticipant = (participant: ParticipantWithSinglePseudonym): string => {
  const isArtistBeneficiary = isParticipantArtistOrBeneficiary(participant);
  const isCompany = isParticipantAmpOrArtCentreOrCMO(participant);

  let displayName = "";
  if (isArtistBeneficiary && !isCompany) {
    displayName = participant.full_name;
  } else if (isCompany) {
    displayName = participant.company_name || "-";
  }

  if (participant.pseudonym) {
    displayName += ` (${participant.pseudonym})`;
  }

  return displayName;
};

// The timezone plugin depends on the utc plugin
dayjs.extend(utc);
dayjs.extend(timezone);

export const convertDateUStoAU = (dateTime: string | Date) => {
  return dayjs(dateTime)
    .tz("America/New_York") // Specify source timezone
    .tz("Australia/Sydney") // Convert to target timezone
    .format("YYYY-MM-DD"); // Format as needed
};

export const isReporter = (isAmp: boolean, isCMO: boolean, isArtCenter: boolean) => {
  return isAmp || isCMO || isArtCenter;
};

export const isAmpCmoArtCentre = (participantTypes?: ParticipantTypes[]): boolean => {
  return (
    participantTypes?.some((type) =>
      [ParticipantTypes.AMP, ParticipantTypes.CMO, ParticipantTypes.ART_CENTRE, ParticipantTypes.AGENT].includes(type),
    ) ?? false
  );
};

export const isArtistBeneficiary = (participantTypes?: ParticipantTypes[]): boolean => {
  return (
    participantTypes?.some((type) => [ParticipantTypes.ARTIST, ParticipantTypes.BENEFICIARY].includes(type)) ?? false
  );
};
